import useAddressSuggestions from '../hooks/useAddressSuggestions'
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  useMediaQuery,
  ButtonGroup,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormHelperText,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import objectStore from '../stores/objectStore';
import TagSelector from './settings/tags/TagSelector';
import { observer } from 'mobx-react-lite';
import FileEditUpload from './FileEditUpload';
import FileEditUploadParse from './FileEditUploadParse';
import { config } from '../config';
import logger from '../utils/logger';
import statusStore from '../stores/statusStore';
import apiClient from '../utils/apiClient';
import i18nStore from '../stores/i18nStore';

const DynamicFieldsEditParsed = React.forwardRef(({ object, fields, objectRaw }, ref) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  const [initialValues, setInitialValues] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [selectedTags, setSelectedTags] = useState([]);
  const [errors, setErrors] = useState({});
  const [wasValidated, setWasValidated] = useState(false);

  // Состояние для адреса
  const [address, setAddress] = useState('');
  const addressLookup = useAddressSuggestions();
  const { suggestions: addressSuggestions, loading: isLoadingAddressSuggestions, open: openAddressSuggestions, setOpen: setOpenAddressSuggestions, error: addressError } = addressLookup;
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isAddressSelectedFlag, setIsAddressSelectedFlag] = useState(false);
  const addressInputRef = useRef(null);
  const addressRevision = useRef(0);

  const [selectedColleagues, setSelectedColleagues] = useState([]);

  // Состояние для фотографий
  const [updatedPhotos, setUpdatedPhotos] = useState(null);

  const objectId = object.id;

  // Стейт для значений всех полей
  useEffect(() => {
    const controller = new AbortController();
    const revision = ++addressRevision.current;
    // Здесь собираем начальные значения для всех полей
    const init = {};
    Object.keys(fields).forEach((key) => {
      // Если значение есть на верхнем уровне, используем его,
      // иначе ищем в details или задаем пустую строку.
      init[key] =
        object[key] !== undefined ? object[key] : (object.details && object.details[key]) || '';
    });

    // Установка адреса из объекта
    if (object.address) {
      logger.log('Инициализация адреса из объекта:', object.address);
      setAddress(object.address);
      setIsAddressSelectedFlag(true);

      // Если есть структурированные данные адреса
      if (object.addressData) {
        setSelectedAddress(object.addressData);
      } else {
        // Если addressData нет, но есть адрес, попробуем запросить данные по нему
        (async () => {
          try {
            const response = await apiClient.post('/api/dadata/suggest', {
              query: object.address,
            }, { signal: controller.signal });

            if (controller.signal.aborted || revision !== addressRevision.current) return;

            if (response.data && response.data.length > 0) {
              // Если нашли подходящий адрес, используем его
              const firstMatch = response.data[0];
              setSelectedAddress(firstMatch.data);

              // Если точное совпадение, меняем адрес на полученный из DaData
              if (firstMatch.value === object.address) {
                setAddress(firstMatch.value);
              }
            }
          } catch {
            // Optional enrichment must not log request credentials or overwrite user input.
          }
        })();
      }
    }

    setInitialValues(init);
    setFieldValues(init);

    // Инициализируем теги из object.details.tags, если они существуют
    if (object.details && object.details.tags) {
      // Здесь убедитесь, что формат тегов соответствует ожидаемому в TagSelector
      // Tags are referenced by organization-scoped id; TagSelector supplies display fields.
      // Получим полные данные тегов из statusStore, используя id из object.details.tags
      const fullTags = object.details.tags.map((tag) => {
        const fullTag = statusStore.tags.find((storeTag) => storeTag.id === tag.id);
        return fullTag || tag; // Если тег не найден в хранилище, используем исходный
      });

      setSelectedTags(fullTags);
    }
    return () => controller.abort();
  }, [object, fields]);

  // Добавим useEffect для логирования фотографий при инициализации
  useEffect(() => {
    if (object && object.photos && object.photos.length > 0) {
      logger.log('Фотографии объекта:', object.photos);
    }
  }, [object.photos]);

  // Функция для обработки изменений в поле адреса
  const handleAddressChange = (value) => {
    addressRevision.current += 1;
    setAddress(value);
    setIsAddressSelectedFlag(false);
    setSelectedAddress(null);
    addressLookup.search(value);
  };

  // Обработчик выбора адреса из подсказок
  const handleSuggestionClick = (suggestion) => {
    addressRevision.current += 1;
    const newValue = suggestion.value;
    const newData = suggestion.data;
    setAddress(newValue);
    setSelectedAddress(newData);
    setIsAddressSelectedFlag(true);
    addressLookup.clear();

    // Обновляем fieldValues для сохранения при отправке
    setFieldValues((prev) => ({
      ...prev,
      address: newValue,
      addressData: newData,
    }));
  };

  // Обработчик для текстовых полей, текстовых областей и select
  const handleChange = (key, value) => {
    // Просто сохраняем значение как есть, без преобразования типа
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const shouldDisplayField = (field, values) => {
    const { dependencies, hideWhen } = field;

    // 1. Check dependencies (SHOW logic - OR for array format, AND for object format)
    let showBasedOnDependencies = true;
    if (dependencies) {
      const checkCondition = (depField, depValues) => {
        const actualValue = values[depField];
        if (Array.isArray(actualValue)) {
          // multiple = true
          return actualValue.some((val) => depValues.includes(val));
        }
        // multiple = false (single value)
        return depValues.includes(actualValue);
      };

      if (Array.isArray(dependencies)) {
        // Old array format (OR logic between conditions)
        showBasedOnDependencies = dependencies.some((condition) =>
          Object.entries(condition).every(([depField, depValues]) =>
            checkCondition(depField, depValues),
          ),
        );
      } else {
        // New object format (AND logic between conditions)
        showBasedOnDependencies = Object.entries(dependencies).every(([depField, depValues]) =>
          checkCondition(depField, depValues),
        );
      }
    }

    // If dependencies condition says hide, then hide.
    if (!showBasedOnDependencies) {
      return false;
    }

    // 2. Check hideWhen (HIDE if ALL conditions met - AND logic)
    if (hideWhen) {
      const allHideConditionsMet = Object.entries(hideWhen).every(([depField, depValues]) => {
        const actualValue = values[depField];
        // Check if the actual value matches *any* of the values specified for hiding in depValues
        if (Array.isArray(actualValue)) {
          // multiple = true
          // If *any* selected value is in the hide list for this field, this condition is true
          return actualValue.some((val) => depValues.includes(val));
        }
        // multiple = false (single value)
        return depValues.includes(actualValue); // If the single value is in the hide list, this condition is true
      });

      // If all hide conditions were met, hide the field.
      if (allHideConditionsMet) {
        return false;
      }
    }

    // If we reached here, dependencies allow showing and hideWhen conditions were not all met.
    return true;
  };

  // Группируем поля по group
  const groupedFields = Object.entries(fields).reduce((acc, [key, field]) => {
    const group = field.group || key; // Если group не задан, используем ключ как отдельную группу
    if (!acc[group]) acc[group] = [];
    acc[group].push({ key, ...field });
    return acc;
  }, {});

  // Обработчик для переключения значений кнопок
  const handleToggle = (key, optionId, multiple) => {
    setFieldValues((prev) => {
      if (multiple) {
        // Предыдущее значение (массив ID)
        const selectedIds = prev[key] || [];
        // Если в массиве уже есть optionId, убираем его, иначе добавляем
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];

        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        // Одиночное значение
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });

    // Если нужно, аналогично меняем setFieldsFromDynamicFilelds(...)
    setFieldsFromDynamicFilelds((prev) => {
      if (multiple) {
        const selectedIds = prev[key] || [];
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];
        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });
  };

  // Функция для проверки всех обязательных полей
  const validateRequiredFields = () => {
    const newErrors = {};
    let isValid = true;

    logger.log(
      'Начинаем валидацию. Адрес:',
      address,
      'Выбран из подсказок:',
      isAddressSelectedFlag,
    );

    // Проверка адреса, если он обязателен
    if (fields.address && fields.address.required) {
      if (!address) {
        newErrors.address = 'Адрес обязателен';
        isValid = false;
      } else if (!isAddressSelectedFlag) {
        newErrors.address = 'Пожалуйста, выберите адрес из подсказок';
        isValid = false;
      }
    }

    // Проходим по всем полям
    Object.entries(fields)
      .filter(([key, field]) => shouldDisplayField(field, fieldValues)) // Только видимые поля
      .forEach(([key, field]) => {
        // Проверяем только поля с required: true
        if (field.required) {
          let fieldValid = true;

          // Пропускаем адрес, так как его проверили выше
          if (key === 'address') {
            return;
          }

          // Проверка для разных типов полей
          if (field.controlType === 'buttons' || field.controlType === 'dropdown') {
            if (field.multiple) {
              // Если поле множественного выбора, проверяем что массив не пустой
              fieldValid = Array.isArray(fieldValues[key]) && fieldValues[key].length > 0;
            } else {
              // Если поле одиночного выбора, проверяем что значение существует
              fieldValid =
                fieldValues[key] !== undefined &&
                fieldValues[key] !== null &&
                fieldValues[key] !== '';
            }
          } else if (field.controlType === 'text' || field.controlType === 'textarea') {
            // Для текстовых полей проверяем наличие и не пустоту строки
            fieldValid =
              fieldValues[key] !== undefined &&
              fieldValues[key] !== null &&
              String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'number' || field.controlType === 'float') {
            // Для числовых полей проверяем наличие значения
            fieldValid =
              fieldValues[key] !== undefined &&
              fieldValues[key] !== null &&
              String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'increment') {
            // Для increment проверяем, что значение больше 0
            fieldValid =
              fieldValues[key] !== undefined && fieldValues[key] !== null && fieldValues[key] > 0;
          }

          // Если поле не прошло валидацию
          if (!fieldValid) {
            newErrors[key] = 'Это поле обязательно для заполнения';
            isValid = false;
            logger.warn(`Поле ${key} не прошло валидацию`);
          }
        }
      });

    // Обновляем состояние ошибок
    setErrors(newErrors);
    setWasValidated(true);

    if (!isValid) {
      logger.warn('Валидация не пройдена. Ошибки:', newErrors);
    } else {
      logger.log('Валидация пройдена успешно');
    }

    return isValid;
  };

  // Обработчик изменения фотографий
  const handlePhotosChange = (newPhotos) => {
    logger.log('Обновление списка фотографий:', newPhotos);
    setUpdatedPhotos(newPhotos);
  };

  const handleSubmit = () => {
    logger.log('Функция в дочернем компоненте вызвана!');

    // Проверяем заполнение обязательных полей перед отправкой
    if (!validateRequiredFields()) {
      logger.error('Не все обязательные поля заполнены');
      return false; // Возвращаем false, если валидация не прошла
    }

    // Собираем измененные поля
    const patch = Object.keys(fieldValues).reduce((acc, key) => {
      const field = fields[key];

      // Пропускаем зависимые поля, которые не должны отображаться
      if (field && field.dependencies && !shouldDisplayField(field, fieldValues)) {
        if (String(fieldValues[key]) !== '') {
          acc[key] = '';
        }
        return acc;
      }

      // Только измененные значения
      if (String(fieldValues[key]) !== String(initialValues[key])) {
        // Конвертируем типы только при отправке
        if (field.controlType === 'number' && fieldValues[key] !== '') {
          const intValue = parseInt(fieldValues[key], 10);
          acc[key] = isNaN(intValue) ? 0 : intValue;
        } else if (field.controlType === 'float' && fieldValues[key] !== '') {
          const floatValue = parseFloat(fieldValues[key]);
          acc[key] = isNaN(floatValue) ? 0 : floatValue;
        } else {
          acc[key] = fieldValues[key];
        }
      }
      return acc;
    }, {});

    // Добавляем теги
    patch.tags = selectedTags;

    // Обработка адреса
    if (address) {
      if (isAddressSelectedFlag) {
        // Если адрес выбран из подсказок и отличается от исходного
        if (address !== object.address) {
          logger.log('Адрес изменен и выбран из подсказок:', address);
          patch.address = address;
          patch.addressData = selectedAddress;
        }
      } else {
        // Если адрес введен вручную, но не выбран из подсказок - предупреждаем в логе
        logger.warn('Адрес введен, но не выбран из подсказок:', address);
      }
    } else if (object.address && address === '') {
      // Если адрес был очищен - тоже отправляем пустое значение
      logger.log('Адрес был очищен');
      patch.address = '';
      patch.addressData = null;
    }

    // Если были изменения в фотографиях, добавляем их в patch
    if (updatedPhotos !== null) {
      logger.log('Сохраняем обновленные фотографии:', updatedPhotos);

      // Разделяем новые и существующие фотографии
      const existingPhotos = updatedPhotos.filter((photo) => !photo.isNew);
      const newPhotos = updatedPhotos.filter((photo) => photo.isNew);

      // Логика для отправки существующих и новых фотографий
      // В реальном приложении здесь будет код для загрузки новых фотографий на сервер
      // и обновления существующих
      patch.photos = updatedPhotos;

      // Если есть новые фото (с полем file), их нужно будет загрузить отдельно
      if (newPhotos.length > 0) {
        logger.log('Новые фотографии для загрузки:', newPhotos);
        patch.newPhotos = newPhotos;
      }
    }

    logger.log('Patch для отправки:', patch);
    const objectId = object.id;
    objectStore.editObject(objectId, patch);

    return true; // Возвращаем true, если все прошло успешно
  };

  // Новая функция для добавления объекта из парсера
  const postNewParsedObject = async () => {
    logger.log('Функция postNewParsedObject вызвана!');

    // Проверяем заполнение обязательных полей перед отправкой
    if (!validateRequiredFields()) {
      logger.error('Не все обязательные поля заполнены');
      return { success: false }; // Возвращаем false, если валидация не прошла
    }

    // Собираем данные объекта с непустыми полями
    const newObject = {};

    // Добавляем базовые важные поля из исходного объекта (кроме details)
    if (object.id) newObject.id = object.id;
    if (object.type) newObject.type = object.type;

    // Сохраняем фотографии из исходного объекта
    if (object.photos && object.photos.length > 0) {
      logger.log('Сохраняем оригинальные фотографии из объекта:', object.photos);
      newObject.photos = [...object.photos];
    }

    if (object.importSource) {
      newObject.importSource = { ...object.importSource };
    }

    // Если есть details, добавляем все поля из details на верхний уровень
    if (object.details) {
      Object.entries(object.details).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          newObject[key] = value;
        }
      });
    }

    // Обновляем поля из формы, добавляя только непустые значения
    Object.keys(fieldValues).forEach((key) => {
      const field = fields[key];
      const value = fieldValues[key];

      // Пропускаем поля, которых нет в fields
      if (!field) {
        // Но всё равно добавляем значение, если это не адресные данные
        if (key !== 'addressData' && value !== null && value !== undefined && value !== '') {
          newObject[key] = value;
        }
        return;
      }

      // Пропускаем зависимые поля, которые не должны отображаться
      if (field.dependencies && !shouldDisplayField(field, fieldValues)) {
        return;
      }

      // Проверяем, что значение не пустое
      if (value === null || value === undefined || value === '') {
        return; // Пропускаем пустые поля
      }

      // Для массивов проверяем, что они не пустые
      if (Array.isArray(value) && value.length === 0) {
        return; // Пропускаем пустые массивы
      }

      // Конвертируем типы для непустых значений
      if (field.controlType === 'number') {
        const intValue = parseInt(value, 10);
        if (!isNaN(intValue)) {
          newObject[key] = intValue;
        }
      } else if (field.controlType === 'float') {
        const floatValue = parseFloat(value);
        if (!isNaN(floatValue)) {
          newObject[key] = floatValue;
        }
      } else {
        newObject[key] = value;
      }
    });

    // Добавляем теги только если они есть
    if (selectedTags && selectedTags.length > 0) {
      // A selected tag is an organization-scoped resource; preserve only its id.
      const cleanedTags = selectedTags.map((tag) => {
        const { id } = tag;
        return { id };
      });
      newObject.tags = cleanedTags;
    }

    // Добавляем настройки доступа
    newObject.colleagues = 'none';

    // Обработка адреса только если он выбран корректно
    if (address) {
      // Всегда сохраняем введенный адрес в поле address
      newObject.address = address;

      // Если адрес выбран из подсказок и есть данные от Dadata
      if (isAddressSelectedFlag && selectedAddress) {
        // Сохраняем полные данные от Dadata в поле addressData
        newObject.addressData = selectedAddress;
      }
    }

    // Обработка фотографий, которые могли быть обновлены через компонент FileEditUploadParse
    if (updatedPhotos !== null && updatedPhotos.length > 0) {
      logger.log('Обновляем фотографии из updatedPhotos:', updatedPhotos);
      newObject.photos = updatedPhotos;
    }

    // Логируем финальный объект с фотографиями перед отправкой
    logger.log('Финальный объект для добавления:', newObject);
    if (newObject.photos) {
      logger.log('Фотографии в финальном объекте:', newObject.photos);
      logger.log('Количество фотографий:', newObject.photos.length);
    } else {
      logger.log('Фотографии отсутствуют в финальном объекте');
    }

    // Вызываем метод для создания объекта
    return objectStore.createObject(newObject);
  };

  // Экспонируем функции через ref
  React.useImperativeHandle(ref, () => ({
    handleSubmit,
    postNewParsedObject,
  }));

  function setFieldsFromDynamicFilelds() {
    // Пустая реализация, так как метод не используется
  }

  return (
    <div>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log(JSON.stringify(object, null, 2))}>
          % DynamicFieldsEditParsed %
        </div>
      )}
      <TagSelector selectedTags={selectedTags} onChange={setSelectedTags} />
      <div className="mt-4">
        <p>
          <span>Адрес объекта:</span> <span className="text-red-500">{objectRaw.city}, {objectRaw.address}</span>
          <p>адрес нужно добавить вручную в поле ниже</p>
        </p>
      </div>
      <Box sx={{ marginBottom: 2, position: 'relative', mt: 4 }}>
        <Autocomplete
          freeSolo
          filterOptions={(options) => options}
          open={openAddressSuggestions}
          onOpen={() => {
            if (addressSuggestions.length > 0) {
              setOpenAddressSuggestions(true);
            }
          }}
          onClose={() => setOpenAddressSuggestions(false)}
          options={addressSuggestions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
          value={address}
          loading={isLoadingAddressSuggestions}
          loadingText="Загрузка..."
          noOptionsText={address.length > 3 ? 'Нет подходящих адресов' : 'Введите больше символов'}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue !== 'string') {
              handleSuggestionClick(newValue);
            } else if (newValue === null) {
              handleAddressChange('');
            } else if (typeof newValue === 'string') {
              setAddress(newValue);
              setIsAddressSelectedFlag(false);
              setOpenAddressSuggestions(false);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              handleAddressChange(newInputValue);
            } else if (reason === 'clear') {
              handleAddressChange('');
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              ref={addressInputRef}
              label="Адрес"
              size="small"
              fullWidth
              sx={{ maxWidth: '500px' }}
              placeholder={`${i18nStore.currentCountry}  Выберите адрес из подсказок`}
              error={Boolean(addressError) || (address.length > 0 && !isAddressSelectedFlag)}
              helperText={
                addressError || (address.length > 0 && !isAddressSelectedFlag
                  ? 'Пожалуйста, выберите адрес из подсказок'
                  : errors['address'] || '')
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingAddressSuggestions ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      {Object.entries(fields)
        .filter(([key, field]) => shouldDisplayField(field, fieldValues)) // Фильтруем поля по зависимостям
        .map(([key, field]) => {
          // Пропускаем поля типа 'photo', так как у нас уже есть общий компонент для фотографий выше
          if (field.controlType === 'photo') {
            return null; // Не рендерим ничего для этого типа поля
          }

          switch (field.controlType) {
            case 'text':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="text"
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'number':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="number"
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'float':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="number"
                      inputProps={{ step: '0.01' }}
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'empty_divider':
              return <div className="mt-12"></div>;
            case 'header':
              return (
                <div className="mt-10 mb-4">
                  <p className="text-xl font-semibold ">{field.title}</p>
                </div>
              );
            case 'header2':
              return (
                <div className="mt-7">
                  <p className="text-base font-normal">{field.title}</p>
                </div>
              );
            case 'increment':
              return (
                <Box
                  key={key}
                  sx={{
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  <span className="border p-1 border-gray-400 rounded">
                    <label className="ml-2">{field.label}</label>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = Math.max(0, (fieldValues[key] || 0) - 1);
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                        marginBottom: '4px',
                        marginLeft: '20px',
                      }}>
                      -
                    </Button>
                    <span>{fieldValues[key] || 0}</span>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = (fieldValues[key] || 0) + 1;
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                      }}>
                      +
                    </Button>
                  </span>
                  {field.required && (
                    <div className="text-orange-500 font-extrabold text-xl">*</div>
                  )}
                </Box>
              );

            case 'textarea':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    label={field.label}
                    required={field.required}
                    multiline
                    rows={field.rows || 4}
                    value={fieldValues[key] || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    placeholder={field.placeholder || ''}
                    fullWidth={isSmallScreen}
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px',
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                    }}
                    error={!!errors[key]}
                    helperText={errors[key]}
                  />
                  {field.required && (
                    <div className="text-orange-500 font-extrabold text-xl">*</div>
                  )}
                </Box>
              );
            case 'dropdown':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    select
                    label={field.label}
                    size="small"
                    required={field.required}
                    fullWidth={isSmallScreen}
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px',
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                    }}
                    SelectProps={{
                      multiple: field.multiple,
                    }}
                    value={fieldValues[key] ?? (field.multiple ? [] : '')}
                    onChange={(e) => {
                      handleChange(key, e.target.value);
                    }}
                    error={!!errors[key]}
                    helperText={errors[key]}>
                    {field.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              );

            case 'buttons':
              // Проверяем, является ли поле _mainDealType
              if (key === '_mainDealType') {
                // Рендерим кнопки, но делаем их неактивными
                return (
                  <Box key={key} sx={{ marginBottom: 2, marginTop: 3 }}>
                    <label>{field.label}</label>
                    <div className="flex flex-row">
                      <div>
                        <ButtonGroup
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            marginTop: '8px',
                            marginRight: '8px',
                            textTransform: 'none',
                          }}
                          variant="outlined"
                          disabled={true} // Делаем все кнопки неактивными
                        >
                          {field.options.map((option) => {
                            const isSelected = field.multiple
                              ? (fieldValues[key] || []).includes(option.id)
                              : fieldValues[key] === option.id;

                            return (
                              <Button
                                key={option.id}
                                variant={isSelected ? 'contained' : 'outlined'}
                                sx={{
                                  textTransform: 'none',
                                  opacity: 0.7, // Делаем кнопки более серыми
                                }}>
                                {option.value}
                              </Button>
                            );
                          })}
                        </ButtonGroup>
                      </div>
                    </div>
                  </Box>
                );
              }

              return (
                <Box key={key} sx={{ marginBottom: 2, marginTop: 3 }}>
                  <label>
                    {field.label}
                    {/* 	{field.required && ' *'} */}
                  </label>

                  <div className="flex flex-row">
                    <div>
                      <ButtonGroup
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          marginTop: '8px',
                          marginRight: '8px',
                          textTransform: 'none',
                        }}
                        variant="outlined">
                        {field.options.map((option) => {
                          const isSelected = field.multiple
                            ? (fieldValues[key] || []).includes(option.id)
                            : fieldValues[key] === option.id;

                          return (
                            <Button
                              key={option.id}
                              variant={isSelected ? 'contained' : 'outlined'}
                              sx={{ textTransform: 'none' }}
                              onClick={() => handleToggle(key, option.id, field.multiple)}>
                              {option.value}
                            </Button>
                          );
                        })}
                      </ButtonGroup>
                      {errors[key] && <FormHelperText error>{errors[key]}</FormHelperText>}
                    </div>
                    {field.required && (
                      <div className="text-orange-500 font-extrabold text-xl">*</div>
                    )}
                  </div>
                </Box>
              );

            case 'checkbox':
              return (
                <Box key={key} sx={{ marginBottom: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!fieldValues[key]}
                        onChange={(e) => handleChange(key, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                  {errors[key] && <FormHelperText error>{errors[key]}</FormHelperText>}
                </Box>
              );
            default:
              return null;
          }
        })}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '10vh',
          paddingBottom: '70px',
        }}>
        {/* Отображение компонента для работы с фотографиями */}
        <Box sx={{ marginBottom: 4, marginTop: 2 }}>
          <FileEditUploadParse
            photos={object.photos || []}
            title={{ label: 'Фотографии объекта' }}
            onPhotosChange={handlePhotosChange}
          />
        </Box>
        {wasValidated && Object.keys(errors).length > 0 && (
          <Box
            sx={{
              padding: 2,
              marginBottom: 2,
              backgroundColor: '#FEE2E2',
              borderRadius: 1,
              color: '#B91C1C',
            }}>
            <Typography variant="body1" fontWeight="medium">
              Пожалуйста, заполните все обязательные поля, отмеченные звездочкой (*).
            </Typography>
          </Box>
        )}
      </div>
    </div>
  );
});

DynamicFieldsEditParsed.displayName = 'DynamicFieldsEditParsed';

export default observer(DynamicFieldsEditParsed);
